import { useState, useMemo } from 'react';
import {
  Card,
  CardBody,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useDisclosure,
  Select,
  SelectItem,
  Tooltip,
  Spinner,
} from '@heroui/react';
import {
  MdNotifications,
  MdVisibility,
  MdCheckCircle,
  MdMessage,
  MdComment,
  MdThumbUp,
  MdAlternateEmail,
  MdWarning,
  MdInfo,
} from 'react-icons/md';
import { IoSearchSharp } from 'react-icons/io5';

import { motion, AnimatePresence } from 'framer-motion';
import {
  NotificationPriority,
  NotificationType,
  Notification,
} from '../../../../utils/type';
import {
  useNotifications,
  useUnreadNotifications,
} from '../../../../hook/useQueryData';
import {
  useMarkAllNotificationsAsRead,
  useMarkNotificationAsRead,
} from '../../../../hook/useMutationData';
import PageHeader from '../../../components/common/ui/PageHeader';
import CustomInput from '../../../components/CustomInput';
import CustomButton from '../../../components/CustomButton';
import CustomChip from '../../../components/CustomChip';

/* ------------------------------------------------------------------ */
/* Helper Functions */
/* ------------------------------------------------------------------ */

const getNotificationTypeIcon = (type: NotificationType) => {
  switch (type) {
    case 'system':
      return <MdNotifications className="text-primary" size={16} />;
    case 'message':
      return <MdMessage className="text-secondary" size={16} />;
    case 'comment':
      return <MdComment className="text-warning" size={16} />;
    case 'like':
      return <MdThumbUp className="text-pink-500" size={16} />;
    case 'mention':
      return <MdAlternateEmail className="text-success" size={16} />;
    case 'alert':
      return <MdWarning className="text-destructive" size={16} />;
    case 'other':
      return <MdInfo className="text-gray-500" size={16} />;
    default:
      return <MdNotifications className="text-gray-500" size={16} />;
  }
};

const getNotificationTypeColor = (type: NotificationType) => {
  switch (type) {
    case 'system':
      return 'primary';
    case 'message':
      return 'secondary';
    case 'comment':
      return 'warning';
    case 'like':
      return 'danger';
    case 'mention':
      return 'success';
    case 'alert':
      return 'danger';
    case 'other':
      return 'default';
    default:
      return 'default';
  }
};

const getNotificationTypeLabel = (type: NotificationType) => {
  switch (type) {
    case 'system':
      return 'System';
    case 'message':
      return 'Message';
    case 'comment':
      return 'Comment';
    case 'like':
      return 'Like';
    case 'mention':
      return 'Mention';
    case 'alert':
      return 'Alert';
    case 'other':
      return 'Other';
    default:
      return 'Unknown';
  }
};

const getPriorityColor = (priority: NotificationPriority) => {
  switch (priority) {
    case 'critical':
      return 'danger';
    case 'high':
      return 'warning';
    case 'medium':
      return 'secondary';
    case 'low':
      return 'success';
    default:
      return 'default';
  }
};

const getNotificationPriorityLabel = (priority: NotificationPriority) => {
  switch (priority) {
    case 'critical':
      return 'Critical';
    case 'high':
      return 'High';
    case 'medium':
      return 'Medium';
    case 'low':
      return 'Low';
    default:
      return 'Unknown';
  }
};

const getNotificationTypeBorderColor = (type: NotificationType) => {
  switch (type) {
    case 'system':
      return 'border-l-primary';
    case 'message':
      return 'border-l-secondary';
    case 'comment':
      return 'border-l-warning';
    case 'like':
      return 'border-l-destructive';
    case 'mention':
      return 'border-l-success';
    case 'alert':
      return 'border-l-destructive border-l-red-500';
    case 'other':
      return 'border-l-gray-400';
    default:
      return 'border-l-gray-400';
  }
};

const formatDateYMD = (dateString: string) => {
  const date = new Date(dateString);
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}/${m}/${d}`;
};

const formatTime = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
};

const getRelativeTime = (dateString: string) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  const diffWeeks = Math.floor(diffDays / 7);

  // Calculate months and years more accurately
  let diffMonths = (now.getFullYear() - date.getFullYear()) * 12;
  diffMonths += now.getMonth() - date.getMonth();

  // Adjust for days if the current day is earlier in the month
  if (now.getDate() < date.getDate()) {
    diffMonths--;
  }

  const diffYears = Math.floor(diffMonths / 12);

  if (diffMins < 60) {
    return `${diffMins}m ago`;
  } else if (diffHours < 24) {
    return `${diffHours}h ago`;
  } else if (diffDays < 7) {
    return `${diffDays}d ago`;
  } else if (diffWeeks < 4) {
    return `${diffWeeks}w ago`;
  } else if (diffMonths < 12) {
    return `${diffMonths}mo ago`;
  } else {
    return `${diffYears}y ago`;
  }
};

/* ------------------------------------------------------------------ */
/* Component */
/* ------------------------------------------------------------------ */

const Notifications = () => {
  const { isOpen, onOpen, onOpenChange } = useDisclosure();

  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<NotificationType | ''>('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [readFilter, setReadFilter] = useState('');
  const [sortDirection, setSortDirection] = useState<'newest' | 'oldest'>(
    'newest'
  );
  const [selectedNotification, setSelectedNotification] =
    useState<Notification | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [limit] = useState(100);

  const filterParams = useMemo(
    () => ({
      type: typeFilter || undefined,
      priority: priorityFilter || undefined,
      readStatus:
        readFilter === 'read'
          ? 'read'
          : readFilter === 'unread'
            ? 'unread'
            : undefined,
      sortBy: sortDirection,
      search: search || undefined,
      limit: limit,
      page: currentPage,
    }),
    [
      typeFilter,
      priorityFilter,
      readFilter,
      sortDirection,
      search,
      limit,
      currentPage,
    ]
  );

  // Fetch notifications from API - UPDATED: removed limit, page, type parameters
  const {
    data: apiNotificationsData,
    isLoading: isLoadingApi,
    refetch: refetchNotifications,
  } = useNotifications(
    {
      enabled: true,
    },
    filterParams
  );

  // Fetch unread count from API
  const { refetch: refetchUnread } = useUnreadNotifications();

  // Mutations
  const markAsReadMutation = useMarkNotificationAsRead();
  const markAllAsReadMutation = useMarkAllNotificationsAsRead();

  // Get notifications from API response
  const notifications = useMemo(() => {
    if (apiNotificationsData?.data) {
      return apiNotificationsData.data.map((notification: any) => ({
        ...notification,
        _id: notification._id || notification.id,
        isRead: notification.read || notification.isRead || false,
      })) as Notification[];
    }
    return [];
  }, [apiNotificationsData]);

  const pagination = useMemo(() => {
    return (
      apiNotificationsData?.pagination || {
        total: 0,
        page: 1,
        limit: 100,
        totalPages: 0,
      }
    );
  }, [apiNotificationsData]);

  // Calculate total unread count from API data
  const unreadCount = useMemo(() => {
    return notifications.filter((n) => !(n.isRead || n.read)).length;
  }, [notifications]);

  /* ------------------------------------------------------------------ */
  /* Event Handlers */
  /* ------------------------------------------------------------------ */

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
    setCurrentPage(1);
  };

  const handleTypeFilterChange = (keys: any) => {
    const selectedKey = Array.from(keys)[0] as NotificationType | '';
    setTypeFilter(selectedKey || '');
    setCurrentPage(1);
  };

  const handlePriorityFilterChange = (keys: any) => {
    const selectedKey = Array.from(keys)[0] as string;
    setPriorityFilter(selectedKey || '');
    setCurrentPage(1);
  };

  const handleReadFilterChange = (keys: any) => {
    const selectedKey = Array.from(keys)[0] as string;
    setReadFilter(selectedKey || '');
    setCurrentPage(1);
  };

  const handleSortDirectionChange = (keys: any) => {
    const selectedKey = Array.from(keys)[0] as 'newest' | 'oldest';
    setSortDirection(selectedKey || 'newest');
    setCurrentPage(1);
  };

  const handleViewNotification = (notification: Notification) => {
    setSelectedNotification(notification);
    onOpen();
  };

  const handleMarkAsRead = (notification: Notification) => {
    const isUnread = !(notification.isRead || notification.read);

    if (isUnread) {
      markAsReadMutation.mutate(notification._id, {
        onSuccess: () => {
          // Refetch notifications to get updated read status
          refetchNotifications();
          refetchUnread();
        },
      });
    }
  };

  const handleMarkAllAsRead = () => {
    // Mark all notifications as read via API
    markAllAsReadMutation.mutate(undefined, {
      onSuccess: () => {
        // Refetch notifications to get updated read status
        refetchNotifications();
        refetchUnread();
      },
    });
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const TimelineView = () => {
    if (isLoadingApi && notifications.length === 0) {
      return (
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <Spinner size="lg" color="primary" />
            <p className="mt-4 text-gray-600">Loading notifications...</p>
          </div>
        </div>
      );
    }

    if (notifications.length === 0) {
      return (
        <Card className="p-4 sm:p-8 text-center">
          <CardBody className="p-4">
            <MdNotifications size={48} className="mx-auto text-gray-400 mb-4" />
            <h3 className="text-md sm:text-xl mb-2 text-gray-400 mx-auto">
              No notifications found
            </h3>
            <p className="text-sm sm:text-base text-gray-500 mb-4 mx-auto max-w-md">
              {search || typeFilter || priorityFilter || readFilter
                ? 'Try changing your filters to see more notifications.'
                : 'All caught up! You have no notifications.'}
            </p>
          </CardBody>
        </Card>
      );
    }

    // Group notifications by date
    const groupedNotifications = notifications.reduce(
      (acc, n) => {
        const dateKey = n.createdAt.split('T')[0];
        acc[dateKey] = acc[dateKey] || [];
        acc[dateKey].push(n);
        return acc;
      },
      {} as Record<string, Notification[]>
    );

    return (
      <div className="space-y-6">
        {Object.entries(groupedNotifications).map(
          ([dateKey, dayNotifications]) => {
            const date = new Date(dateKey);
            const isToday = date.toDateString() === new Date().toDateString();
            const isYesterday =
              date.toDateString() ===
              new Date(Date.now() - 86400000).toDateString();

            let dateLabel;
            if (isToday) {
              dateLabel = 'Today';
            } else if (isYesterday) {
              dateLabel = 'Yesterday';
            } else {
              dateLabel = date.toLocaleDateString([], {
                weekday: 'long',
                month: 'long',
                day: 'numeric',
                year: 'numeric',
              });
            }

            return (
              <div key={dateKey} className="relative">
                {/* Date Header */}
                <div className="flex items-center gap-4 mb-4">
                  <div className="flex items-center gap-3">
                    <div
                      className="h-8 w-8 border-2 border-white shadow-sm rounded-lg flex flex-col items-center justify-center text-white"
                      style={{ backgroundColor: '#317690' }}
                    >
                      <div className="text-xs font-medium">
                        {date
                          .toLocaleDateString([], { month: 'short' })
                          .toUpperCase()}
                      </div>
                      <div className="text-sm font-bold leading-none">
                        {date.getDate()}
                      </div>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900">
                        {dateLabel}
                      </h3>
                      <p className="text-xs text-gray-500">
                        {dayNotifications.length} notification
                        {dayNotifications.length !== 1 ? 's' : ''}
                      </p>
                    </div>
                  </div>
                  <div className="flex-1 h-px bg-gradient-to-r from-gray-300 to-transparent" />
                </div>

                {/* Notifications for this date */}
                <div className="space-y-2 ml-5">
                  {dayNotifications.map((notification, noteIndex) => {
                    const timeFormatted = formatTime(notification.createdAt);
                    const isUnread = !(
                      notification.isRead || notification.read
                    );
                    const borderColorClass = getNotificationTypeBorderColor(
                      notification.type
                    );

                    return (
                      <div key={notification._id} className="relative">
                        {/* Timeline connector */}
                        {noteIndex !== dayNotifications.length - 1 && (
                          <div className="absolute left-4 top-8 bottom-0 w-px bg-gray-300" />
                        )}

                        {/* Notification card */}
                        <div className="flex gap-2 group">
                          {/* Time indicator */}
                          <div className="relative flex-shrink-0">
                            <div className="h-8 w-8 border-2 border-white shadow-sm rounded-lg bg-white flex items-center justify-center">
                              <div className="text-xs font-medium text-gray-600 text-center">
                                {timeFormatted}
                              </div>
                            </div>
                            {isUnread && (
                              <div className="absolute -top-1 -right-1 h-2 w-2 bg-danger-300 rounded-full border-2 border-white" />
                            )}
                          </div>

                          {/* Notification content card - USES TYPE-SPECIFIC BORDER COLOR */}
                          <Card
                            className={`flex-1 border-l-4 ${borderColorClass} hover:shadow-sm transition-all duration-200 group-hover:scale-[1.005] min-h-[60px] max-h-[60px] ${
                              isUnread ? 'ring-1 ring-blue-100' : ''
                            }`}
                          >
                            <div className="flex h-full items-center px-3 py-1">
                              {/* Left content area */}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                  {getNotificationTypeIcon(notification.type)}
                                  <h3 className="text-xs font-semibold truncate text-gray-900">
                                    {notification.title}
                                  </h3>

                                  {/* Type tag */}
                                  <CustomChip
                                    label={getNotificationTypeLabel(
                                      notification.type
                                    )}
                                    size="sm"
                                    variant="flat"
                                    color={getNotificationTypeColor(
                                      notification.type
                                    )}
                                    className="text-xs px-1 py-0 h-5"
                                  />

                                  {/* Priority tag */}
                                  <CustomChip
                                    label={getNotificationPriorityLabel(
                                      notification.priority
                                    )}
                                    size="sm"
                                    variant="flat"
                                    color={getPriorityColor(
                                      notification.priority
                                    )}
                                    className="text-xs px-1 py-0 h-5"
                                  />

                                  {isUnread && (
                                    <CustomChip
                                      size="sm"
                                      variant="solid"
                                      color="primary"
                                      label="New"
                                      className="h-5 animate-pulse"
                                    />
                                  )}
                                </div>

                                {/* Additional info */}
                                <div className="flex items-center gap-2 text-xs text-gray-500 truncate">
                                  <span className="font-medium">
                                    {formatDateYMD(notification.createdAt)}
                                    <span> • </span>
                                    <span className="text-gray-400">
                                      {getRelativeTime(notification.createdAt)}
                                    </span>
                                  </span>
                                </div>
                              </div>

                              {/* Right action buttons */}
                              <div className="flex items-center gap-1 border-l border-gray-200 pl-2 ml-2">
                                {/* View Details Button */}
                                <Tooltip
                                  content="View Details"
                                  placement="top"
                                  color="primary"
                                >
                                  <CustomButton
                                    isIconOnly
                                    size="sm"
                                    variant="light"
                                    className="text-gray-500 hover:text-primary min-w-6 h-6"
                                    onPress={() =>
                                      handleViewNotification(notification)
                                    }
                                  >
                                    <MdVisibility size={14} />
                                  </CustomButton>
                                </Tooltip>

                                {/* Mark as Read Button (only for unread) */}
                                {isUnread && (
                                  <Tooltip
                                    content="Mark as Read"
                                    placement="top"
                                    color="success"
                                  >
                                    <CustomButton
                                      isIconOnly
                                      size="sm"
                                      variant="light"
                                      className="text-gray-500 hover:text-green-600 min-w-6 h-6"
                                      onPress={() =>
                                        handleMarkAsRead(notification)
                                      }
                                      isLoading={
                                        markAsReadMutation.isPending &&
                                        markAsReadMutation.variables ===
                                          notification._id
                                      }
                                    >
                                      <MdCheckCircle size={14} />
                                    </CustomButton>
                                  </Tooltip>
                                )}
                              </div>
                            </div>
                          </Card>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          }
        )}
      </div>
    );
  };

  /* ------------------------------------------------------------------ */
  /* Render */
  /* ------------------------------------------------------------------ */

  return (
    <>
      <PageHeader
        title="Notifications"
        description={
          <span>
            {pagination.total} total notifications{' '}
            <span className="text-danger-300 font-medium">
              • {unreadCount} unread
            </span>
            {isLoadingApi && (
              <span className="ml-2 text-xs text-gray-500">
                <Spinner size="sm" className="mr-1" />
                Syncing...
              </span>
            )}
          </span>
        }
        components={
          <div className="flex gap-2 w-full sm:w-auto">
            <CustomInput
              type="text"
              placeholder="Search notifications..."
              value={search}
              onChange={handleSearchChange}
              endContent={<IoSearchSharp size={18} />}
              className="w-full sm:w-[300px]"
              classNames={{
                input: 'placeholder:text-xs',
              }}
            />
          </div>
        }
      />

      {/* Filters and Actions */}
      <Card className="mb-6">
        <CardBody>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* Notification Type Filter - Decreased font size */}
              <Select
                placeholder="All notification types"
                selectedKeys={typeFilter ? [typeFilter] : []}
                onSelectionChange={handleTypeFilterChange}
                variant="bordered"
                color="primary"
                classNames={{
                  trigger: 'min-h-10 h-10',
                  value: 'text-xs',
                  listbox: 'text-xs',
                }}
              >
                <SelectItem key="" className="text-xs">
                  All Types
                </SelectItem>
                <SelectItem key="system" className="text-xs">
                  System
                </SelectItem>
                <SelectItem key="message" className="text-xs">
                  Message
                </SelectItem>
                <SelectItem key="comment" className="text-xs">
                  Comment
                </SelectItem>
                <SelectItem key="like" className="text-xs">
                  Like
                </SelectItem>
                <SelectItem key="mention" className="text-xs">
                  Mention
                </SelectItem>
                <SelectItem key="alert" className="text-xs">
                  Alert
                </SelectItem>
                <SelectItem key="other" className="text-xs">
                  Other
                </SelectItem>
              </Select>

              {/* Priority Filter - Decreased font size */}
              <Select
                placeholder="All priorities"
                selectedKeys={priorityFilter ? [priorityFilter] : []}
                onSelectionChange={handlePriorityFilterChange}
                variant="bordered"
                color="primary"
                classNames={{
                  trigger: 'min-h-10 h-10',
                  value: 'text-xs',
                  listbox: 'text-xs',
                }}
              >
                <SelectItem key="" className="text-xs">
                  All Priorities
                </SelectItem>
                <SelectItem key="critical" className="text-xs">
                  Critical Priority
                </SelectItem>
                <SelectItem key="high" className="text-xs">
                  High Priority
                </SelectItem>
                <SelectItem key="medium" className="text-xs">
                  Medium Priority
                </SelectItem>
                <SelectItem key="low" className="text-xs">
                  Low Priority
                </SelectItem>
              </Select>

              {/* Read Status Filter - Decreased font size */}
              <Select
                placeholder="All status"
                selectedKeys={readFilter ? [readFilter] : []}
                onSelectionChange={handleReadFilterChange}
                variant="bordered"
                color="primary"
                classNames={{
                  trigger: 'min-h-10 h-10',
                  value: 'text-xs',
                  listbox: 'text-xs',
                }}
              >
                <SelectItem key="" className="text-xs">
                  All Status
                </SelectItem>
                <SelectItem key="unread" className="text-xs">
                  Unread Only
                </SelectItem>
                <SelectItem key="read" className="text-xs">
                  Read Only
                </SelectItem>
              </Select>

              {/* Sort Direction Filter - Decreased font size */}
              <Select
                placeholder="Sort by"
                selectedKeys={[sortDirection]}
                onSelectionChange={handleSortDirectionChange}
                variant="bordered"
                color="primary"
                classNames={{
                  trigger: 'min-h-10 h-10',
                  value: 'text-xs',
                  listbox: 'text-xs',
                }}
              >
                <SelectItem key="newest" className="text-xs">
                  Newest First
                </SelectItem>
                <SelectItem key="oldest" className="text-xs">
                  Oldest First
                </SelectItem>
              </Select>
            </div>

            {/* Actions and Active filters */}
            <div className="flex flex-wrap items-center justify-between gap-4 pt-2 border-t">
              <div className="flex flex-wrap items-center gap-2">
                {unreadCount > 0 && (
                  <CustomButton
                    label={`Mark All as Read (${unreadCount})`}
                    size="sm"
                    variant="flat"
                    color="primary"
                    onPress={handleMarkAllAsRead}
                    isLoading={markAllAsReadMutation.isPending}
                    startContent={
                      !markAllAsReadMutation.isPending && (
                        <MdCheckCircle size={16} />
                      )
                    }
                  />
                )}
              </div>

              <div className="flex flex-wrap items-center gap-2">
                {(typeFilter ||
                  priorityFilter ||
                  readFilter ||
                  search ||
                  sortDirection !== 'newest') && (
                  <span className="text-xs text-gray-500">Active filters:</span>
                )}

                {search && (
                  <CustomChip
                    label={`Search: ${search}`}
                    size="sm"
                    variant="flat"
                    onClose={() => {
                      setSearch('');
                      setCurrentPage(1);
                    }}
                  />
                )}

                {typeFilter && (
                  <CustomChip
                    label={`Type: ${getNotificationTypeLabel(typeFilter)}`}
                    size="sm"
                    variant="flat"
                    onClose={() => {
                      setTypeFilter('');
                      setCurrentPage(1);
                    }}
                  />
                )}

                {priorityFilter && priorityFilter !== '' && (
                  <CustomChip
                    label={`Priority: ${getNotificationPriorityLabel(priorityFilter as NotificationPriority)}`}
                    size="sm"
                    variant="flat"
                    onClose={() => {
                      setPriorityFilter('');
                      setCurrentPage(1);
                    }}
                  />
                )}

                {readFilter && readFilter !== '' && (
                  <CustomChip
                    label={`Status: ${readFilter}`}
                    size="sm"
                    variant="flat"
                    onClose={() => {
                      setReadFilter('');
                      setCurrentPage(1);
                    }}
                  />
                )}

                {sortDirection !== 'newest' && (
                  <CustomChip
                    label={`Sort: ${sortDirection}`}
                    size="sm"
                    variant="flat"
                    onClose={() => {
                      setSortDirection('newest');
                      setCurrentPage(1);
                    }}
                  />
                )}
              </div>
            </div>

            {/* Pagination Info */}
            {pagination.totalPages > 1 && (
              <div className="flex justify-between items-center pt-2 border-t">
                <span className="text-sm text-gray-500">
                  Showing {notifications.length} of {pagination.total}{' '}
                  notifications
                </span>
                <div className="flex gap-2">
                  <CustomButton
                    size="sm"
                    variant="flat"
                    onPress={() => handlePageChange(currentPage - 1)}
                    isDisabled={currentPage <= 1}
                  >
                    Previous
                  </CustomButton>
                  <span className="flex items-center text-sm">
                    Page {pagination.page} of {pagination.totalPages}
                  </span>
                  <CustomButton
                    size="sm"
                    variant="flat"
                    onPress={() => handlePageChange(currentPage + 1)}
                    isDisabled={currentPage >= pagination.totalPages}
                  >
                    Next
                  </CustomButton>
                </div>
              </div>
            )}
          </div>
        </CardBody>
      </Card>

      {/* Timeline Content */}
      <TimelineView />

      {/* Notification Details Modal */}
      <Modal
        isOpen={isOpen}
        onOpenChange={onOpenChange}
        size="2xl"
        classNames={{
          base: 'bg-gradient-to-br from-white to-gray-50',
          header: 'border-b border-gray-200',
          body: 'py-6',
          footer: 'border-t border-gray-200',
          closeButton:
            'hover:bg-gray-100 rounded-full transition-all duration-200',
        }}
        motionProps={{
          variants: {
            enter: {
              y: 0,
              opacity: 1,
              transition: {
                duration: 0.3,
                ease: 'easeOut',
              },
            },
            exit: {
              y: -20,
              opacity: 0,
              transition: {
                duration: 0.2,
                ease: 'easeIn',
              },
            },
          },
        }}
      >
        <ModalContent>
          {(onClose) => (
            <>
              {selectedNotification && (
                <>
                  <ModalHeader className="flex flex-col gap-1">
                    <div className="flex items-center gap-3">
                      <div
                        className={`
                        p-2 rounded-xl
                        ${getNotificationTypeColor(selectedNotification.type) === 'primary' ? 'bg-primary-100 text-primary-600' : ''}
                        ${getNotificationTypeColor(selectedNotification.type) === 'secondary' ? 'bg-secondary-100 text-secondary-600' : ''}
                        ${getNotificationTypeColor(selectedNotification.type) === 'success' ? 'bg-success-100 text-success-600' : ''}
                        ${getNotificationTypeColor(selectedNotification.type) === 'warning' ? 'bg-warning-100 text-warning-600' : ''}
                        ${getNotificationTypeColor(selectedNotification.type) === 'danger' ? 'bg-danger-100 text-danger-600' : ''}
                      `}
                      >
                        {getNotificationTypeIcon(selectedNotification.type)}
                      </div>
                      <div>
                        <h2 className="text-xl font-semibold text-gray-900">
                          {selectedNotification.title}
                        </h2>
                        <p className="text-sm text-gray-500 mt-0.5">
                          {selectedNotification.relatedEntity?.name}
                        </p>
                      </div>
                    </div>
                  </ModalHeader>

                  <ModalBody>
                    <AnimatePresence mode="wait">
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.2 }}
                        className="space-y-6"
                      >
                        {/* Type, Priority, and Status Badges */}
                        <div className="flex items-center gap-2 flex-wrap">
                          <CustomChip
                            label={getNotificationTypeLabel(
                              selectedNotification.type
                            )}
                            size="sm"
                            variant="flat"
                            color={getNotificationTypeColor(
                              selectedNotification.type
                            )}
                            className="border-none font-medium px-3 py-1.5"
                          />

                          <CustomChip
                            label={
                              selectedNotification.priority
                                .charAt(0)
                                .toUpperCase() +
                              selectedNotification.priority.slice(1)
                            }
                            size="sm"
                            variant="flat"
                            color={getPriorityColor(
                              selectedNotification.priority
                            )}
                            className="border border-current/20 font-medium px-3 py-1.5"
                          />

                          {!(
                            selectedNotification.isRead ||
                            selectedNotification.read
                          ) && (
                            <CustomChip
                              label="New"
                              size="sm"
                              variant="solid"
                              color="primary"
                              className="animate-pulse font-medium px-3 py-1.5"
                            />
                          )}
                        </div>

                        {/* Message Card */}
                        <div className="bg-gradient-to-br from-gray-50 to-white rounded-xl p-3 border border-gray-200 shadow-sm">
                          <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                            <span className="w-1 h-4 bg-secondary-500 rounded-full"></span>
                            Message
                          </h4>
                          <div className="bg-white rounded-lg p-3 border border-gray-100">
                            <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">
                              {selectedNotification.message}
                            </p>
                          </div>
                        </div>
                      </motion.div>
                    </AnimatePresence>
                  </ModalBody>

                  <ModalFooter>
                    <div className="flex items-center gap-2 w-full justify-between">
                      <div className="flex gap-2">
                        {!(
                          selectedNotification.isRead ||
                          selectedNotification.read
                        ) && (
                          <CustomButton
                            label="Mark as Read"
                            color="success"
                            variant="flat"
                            onPress={() => {
                              handleMarkAsRead(selectedNotification);
                              onClose();
                            }}
                            isLoading={
                              markAsReadMutation.isPending &&
                              markAsReadMutation.variables ===
                                selectedNotification._id
                            }
                          />
                        )}
                      </div>
                      <CustomButton
                        label="Close"
                        variant="light"
                        type="button"
                        onPress={onClose}
                        className="text-danger bg-danger-100"
                      />
                    </div>
                  </ModalFooter>
                </>
              )}
            </>
          )}
        </ModalContent>
      </Modal>
    </>
  );
};

export default Notifications;
